import { useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, DataTable, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { formatLocalDateTime } from './utils/dateTime';

export interface StoreAuditEvent {
  id: string; occurredAt: string; entityType: string; entityKey: string; action: string; outcome: string;
  actorType: string; actorLabel?: string; shopifyUserId?: string; source: string; operation: string;
  correlationId: string; importQueueId?: number; fileName?: string; jobId?: string;
  beforeJson?: string; afterJson?: string; changedFieldsJson?: string;
}
export interface StoreAuditResult { supported: boolean; items: StoreAuditEvent[]; nextBeforeId?: string }

const formatJson = (value?: string) => {
  if (!value) return '—';
  try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
};
const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const utcBoundary = (date: string, end = false) => {
  const value = new Date(`${date}T${end ? '23:59:59.999' : '00:00:00'}`);
  return Number.isNaN(value.valueOf()) ? undefined : value.toISOString();
};

export default function StoreAuditHistory({ shopId, token, onUnauthorized }: { shopId: number; token: string; onUnauthorized: () => void }) {
  const [result, setResult] = useState<StoreAuditResult | null>(null);
  const [actor, setActor] = useState('');
  const [record, setRecord] = useState('');
  const [importId, setImportId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState(localDate(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(localDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const applied = useRef<Record<string, string>>({});
  const sequence = useRef(0);
  const unauthorized = useRef(onUnauthorized); unauthorized.current = onUnauthorized;

  const load = async (filters: Record<string, string>, beforeId?: string) => {
    const request = ++sequence.current;
    setLoading(true); setError('');
    try {
      const response = await agent.Admin.auditHistory(token, shopId, { ...filters, ...(beforeId ? { beforeId } : {}) });
      if (request === sequence.current) setResult(response);
    } catch (err) {
      if (request !== sequence.current) return;
      if (axios.isAxiosError(err) && err.response?.status === 401) unauthorized.current();
      else setError('Unable to load audit history. Try again.');
    } finally { if (request === sequence.current) setLoading(false); }
  };

  useEffect(() => {
    applied.current = {}; setCursors([undefined]); setResult(null);
    setActor(''); setRecord(''); setImportId(''); setAction('');
    setFrom(localDate(new Date(Date.now() - 30 * 86400000))); setTo(localDate(new Date()));
    void load({});
    return () => { sequence.current++; };
    // Filters are submitted explicitly; changing a text field does not issue requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, token]);

  const apply = () => {
    const start = utcBoundary(from); const end = utcBoundary(to, true);
    if (!start || !end || start >= end || (importId && !/^\d+$/.test(importId))) {
      setError('Enter a valid date range and numeric import ID.'); return;
    }
    applied.current = { from: start, to: end, ...(actor ? { actor } : {}), ...(record ? { record } : {}),
      ...(action ? { action } : {}), ...(importId ? { importId } : {}) };
    setCursors([undefined]); setResult(null); void load(applied.current);
  };

  if (result?.supported === false) return <Text as="p">Audit history is available for native PFV2 stores.</Text>;
  return <BlockStack gap="400">
    <Text as="p" tone="subdued">Recorded changes and operation outcomes. Individual users unavailable in older sessions are shown as unknown.</Text>
    <InlineStack gap="300" blockAlign="end" wrap>
      <TextField label="From" type="date" value={from} onChange={setFrom} autoComplete="off" />
      <TextField label="Through" type="date" value={to} onChange={setTo} autoComplete="off" />
      <TextField label="User or staff ID" value={actor} onChange={setActor} autoComplete="off" />
      <TextField label="Record or value" value={record} onChange={setRecord} autoComplete="off" />
      <TextField label="Import ID" value={importId} onChange={setImportId} autoComplete="off" />
      <Select label="Action" value={action} onChange={setAction} options={['', 'Created', 'Updated', 'Deleted', 'Restored', 'Requested', 'Finished', 'ExternalRequest', 'ExternalResult'].map(value => ({ value, label: value || 'All actions' }))} />
      <Button onClick={apply} loading={loading}>Apply</Button>
      <Button onClick={() => void load(applied.current, cursors[cursors.length - 1])} disabled={loading}>Refresh</Button>
    </InlineStack>
    {error && <Banner tone="critical">{error}</Banner>}
    {applied.current.operationId && <Text as="p">Showing related events for operation {applied.current.operationId}. Use Apply to clear this filter.</Text>}
    {result && result.items.length === 0 && <Text as="p">No audit events in this range. History begins when audit logging is enabled.</Text>}
    {result && result.items.length > 0 && <DataTable
      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
      headings={['Time', 'Who', 'Action / outcome', 'Record', 'Source', 'Details']}
      rows={result.items.map(item => [
        formatLocalDateTime(item.occurredAt),
        <div key={`actor-${item.id}`}>{item.actorLabel || item.actorType}<br /><small>{item.actorType}{item.shopifyUserId ? ` · Staff ${item.shopifyUserId}` : ''}</small></div>,
        `${item.action} · ${item.outcome}`,
        <div key={`record-${item.id}`}>{item.entityType}<br /><code>{item.entityKey}</code></div>,
        <div key={`source-${item.id}`}>{item.source}{item.importQueueId ? ` · Import #${item.importQueueId}` : ''}<br />{item.fileName || item.jobId || ''}</div>,
        <details key={`details-${item.id}`}><summary>View changes</summary>
          <div style={{ maxWidth: 560, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            <p>{item.operation}</p><p>Operation: {item.correlationId}</p>
            <Button variant="plain" onClick={() => { applied.current = { ...applied.current, operationId: item.correlationId }; setCursors([undefined]); void load(applied.current); }}>Show related events</Button>
            {item.changedFieldsJson && <p>Changed: {formatJson(item.changedFieldsJson)}</p>}
            {item.entityType === 'Operation' ? <><strong>Details</strong><pre style={{ whiteSpace: 'pre-wrap' }}>{formatJson(item.afterJson)}</pre></>
              : <><strong>Before</strong><pre style={{ whiteSpace: 'pre-wrap' }}>{formatJson(item.beforeJson)}</pre><strong>After</strong><pre style={{ whiteSpace: 'pre-wrap' }}>{formatJson(item.afterJson)}</pre></>}
          </div>
        </details>
      ])} />}
    <InlineStack gap="300">
      <Button disabled={loading || cursors.length < 2} onClick={() => { const previous = cursors.slice(0, -1); setCursors(previous); void load(applied.current, previous[previous.length - 1]); }}>Newer</Button>
      <Button disabled={loading || !result?.nextBeforeId} onClick={() => { const next = result?.nextBeforeId; if (next) { setCursors([...cursors, next]); void load(applied.current, next); } }}>Older</Button>
    </InlineStack>
  </BlockStack>;
}
